var _effectorFileName = "/src/shared/lib/effector/page/page.ts";
import { ParsedUrlQuery } from 'querystring';
import { GetServerSidePropsContext } from 'next';
import { createEvent, createStore, sample, Unit } from 'effector';
import { removeUndefined } from '../../remove-undefined';
export const pageStarted = createEvent<{
  pageType: string;
  pageCtx: unknown;
}>({
  loc: {
    file: _effectorFileName,
    line: 7,
    column: 27
  },
  name: "pageStarted",
  sid: "-2ydcxz"
});
const $currentPage = createStore<string | null>(null, {
  loc: {
    file: _effectorFileName,
    line: 12,
    column: 21
  },
  name: "$currentPage",
  sid: "2ms0h7"
}).on(pageStarted, (_, {
  pageType
}) => pageType);

// serialize: 'ignore', - SSR info won't be exposed to the frontend
export const $pageCookies = createStore<string | null>(null, {
  and: {
    serialize: 'ignore'
  },
  loc: {
    file: _effectorFileName,
    line: 15,
    column: 28
  },
  name: "$pageCookies",
  sid: "b1hccp"
});
export const $pageParsedCookies = createStore<Record<string, string | undefined> | null>(null, {
  and: {
    serialize: 'ignore'
  },
  loc: {
    file: _effectorFileName,
    line: 19,
    column: 34
  },
  name: "$pageParsedCookies",
  sid: "nu433v"
});
export interface PageContext<Q extends ParsedUrlQuery, P> {
  query?: Q | null;
  params?: P | null;
  url?: string;
  cookies?: string;
  parsedCookies?: Record<string, string | undefined>;
}
export const getPageContext = <Q extends ParsedUrlQuery, P>(context: GetServerSidePropsContext): PageContext<Q, P> => {
  return removeUndefined<PageContext<Q, P>>({
    query: (context.query ?? {}) as Q,
    params: (context.params ?? {}) as P,
    url: context.resolvedUrl,
    cookies: context.req.headers.cookie,
    parsedCookies: context.req.cookies
  });
};
export function declarePage<Ctx extends PageContext<any, any>>(config: {
  pageType: string;
  contextContract?: {
    isData: (data: unknown) => data is Ctx;
  };
}) {
  const open = createEvent<Ctx>({
    loc: {
      file: _effectorFileName,
      line: 47,
      column: 15
    },
    name: "open",
    sid: "o1urcu"
  });
  const $ctx = createStore<Ctx | null>(null, {
    and: {
      /**
       * Custom human-readable sid for easier debugging
       *
       * Completely optional practice, can be changed to factories field at
       * effector's babel/swc plugin configuration like this:
       * ```json
       *  factories: ["#root/shared/app"]
       * ```
       *
       * See the docs on SIDs: https://effector.dev/docs/api/effector/babel-plugin#sid
       */
      sid: `pageCtx:${config.pageType}`
    },
    loc: {
      file: _effectorFileName,
      line: 49,
      column: 15
    },
    name: "$ctx",
    sid: "-xxlijh"
  });
  sample({
    and: [{
      clock: open,
      filter: rawCtx => {
        if (config.contextContract) {
          return config.contextContract.isData(rawCtx);
        }

        /**
         * Skip as-is, if no contract is provided
         */

        return true;
      },
      target: [$ctx, pageStarted.prepend((ctx: Ctx) => ({
        pageCtx: ctx,
        pageType: config.pageType
      }))]
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 64,
        column: 2
      },
      sid: "-90bm2x"
    }
  });
  sample({
    and: [{
      clock: $ctx,
      fn: ctx => ctx?.cookies ?? null,
      target: $pageCookies
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 86,
        column: 2
      },
      sid: "-814s3t"
    }
  });
  sample({
    and: [{
      clock: $ctx,
      fn: ctx => ctx?.parsedCookies ?? null,
      target: $pageParsedCookies
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 92,
        column: 2
      },
      sid: "-7mac32"
    }
  });
  const $active = $currentPage.map(page => page === config.pageType);
  const activated = sample({
    and: [{
      clock: $active,
      filter: Boolean
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 99,
        column: 20
      },
      name: "activated",
      sid: "-wnrzcw"
    }
  });
  const deactivated = sample({
    and: [{
      clock: $active,
      filter: active => !active
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 103,
        column: 22
      },
      name: "deactivated",
      sid: "-d1qq9t"
    }
  });
  const opened = createEvent<Ctx>({
    loc: {
      file: _effectorFileName,
      line: 108,
      column: 17
    },
    name: "opened",
    sid: "-jowjv7"
  });
  const closed = createEvent<Ctx>({
    loc: {
      file: _effectorFileName,
      line: 109,
      column: 17
    },
    name: "closed",
    sid: "t1wg7j"
  });
  sample({
    and: [{
      clock: activated,
      source: $ctx,
      /**
       * Type assertion is totally fine here, as we know that $ctx will be of Ctx type at this moment,
       * because it is the way this logic is written and also here we have a contract check for that
       *
       * We can't (yet?) express it in the TypeScript type system though, hence the `as Ctx` cast
       *
       * In real-world apps it is better to write some tests for such cases, rather than
       * writing more complicated code to satisfy the type inference system
       */
      fn: ctx => ctx as Ctx,
      target: opened
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 111,
        column: 2
      },
      sid: "15e7km"
    }
  });
  sample({
    and: [{
      clock: deactivated,
      source: $ctx,
      fn: ctx => ctx as Ctx,
      target: closed
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 127,
        column: 2
      },
      sid: "1pqliz"
    }
  });
  sample({
    and: [{
      clock: closed,
      fn: () => null,
      target: $ctx
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 134,
        column: 2
      },
      sid: "254u53"
    }
  });
  return {
    open,
    opened,
    closed,
    $active
  };
}
export const onlyPage = <T,>(unit: Unit<T>, pageType: string) => {
  return sample({
    and: [{
      clock: unit,
      source: $currentPage,
      filter: page => page === pageType
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 149,
        column: 9
      },
      sid: "2oxfo4"
    }
  });
};